import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash.debounce';

// Load the next page this far from the end. Kept small so the loader appears
// where the user already is, close enough that focusing it barely moves them.
const PREFETCH_PX = 100;
// Leading edge + maxWait, so paging reacts while the user is still scrolling
// instead of only once the scroll stops.
const DEBOUNCE_MS = 100;
const MAX_WAIT_MS = 250;

// Keeps the pagination loader on screen. It renders after the last row — i.e.
// below the fold at the moment it appears — so bring it into view.
export const useLoaderFocus = (isLoading) => {
    const loaderRef = useRef(null);

    useEffect(() => {
        if (isLoading) loaderRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, [isLoading]);

    return loaderRef;
};

/**
 * Bottom-anchored pagination for a scrollable list.
 *
 * One arrival at the bottom loads exactly one page: the handler disarms on
 * trigger and re-arms only once the user scrolls back out of the bottom zone.
 * Without that, anything keeping the list pinned at the bottom — focusing the
 * loader, or a container that sizes itself asynchronously like Masonry — reads
 * as "still at the bottom" and pages until the list runs out.
 *
 * @param {Function} loadMore     Fetches and appends the next page; may be async.
 * @param {Function} [canLoadMore] Returns false when there is nothing left to load.
 * @param {Object}   [containerRef] Scroll container; when given the listener is
 *                                  attached here, otherwise bind `onScroll` yourself.
 * @param {number}   [prefetchPx]  Distance from the end that triggers a load.
 * @returns {{ onScroll: Function, isLoadingMore: boolean, loaderRef: Object, reset: Function }}
 */
const useInfiniteScroll = ({ loadMore, canLoadMore, containerRef, prefetchPx = PREFETCH_PX } = {}) => {
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const loaderRef = useLoaderFocus(isLoadingMore);

    // Refs, not state: state updates are async, so two scroll events in the same
    // tick would both pass a state-based check and request the same page.
    const loadingRef = useRef(false);
    const armedRef = useRef(true);

    // Latest-callback refs keep the debounced handler from going stale without
    // rebuilding it — rebuilding would drop its pending timer on every render.
    const loadMoreRef = useRef(loadMore);
    const canLoadMoreRef = useRef(canLoadMore);
    useEffect(() => {
        loadMoreRef.current = loadMore;
        canLoadMoreRef.current = canLoadMore;
    });

    const checkBottom = useMemo(() => debounce((target) => {
        if (!target) return;

        const nearBottom = (target.scrollTop + target.clientHeight) >= (target.scrollHeight - prefetchPx);
        if (!nearBottom) {
            armedRef.current = true;
            return;
        }
        if (!armedRef.current || loadingRef.current) return;
        if (canLoadMoreRef.current && !canLoadMoreRef.current()) return;

        armedRef.current = false;
        loadingRef.current = true;
        setIsLoadingMore(true);

        (async () => {
            try {
                await loadMoreRef.current?.();
            } catch (error) {
                console.error('Error loading the next page:', error);
            } finally {
                loadingRef.current = false;
                setIsLoadingMore(false);
            }
        })();
    }, DEBOUNCE_MS, { leading: true, maxWait: MAX_WAIT_MS }), [prefetchPx]);

    const onScroll = useCallback((event) => {
        checkBottom(event?.currentTarget || event?.target || containerRef?.current);
    }, [checkBottom, containerRef]);

    const reset = useCallback(() => {
        checkBottom.cancel();
        armedRef.current = true;
        loadingRef.current = false;
    }, [checkBottom]);

    useEffect(() => {
        const container = containerRef?.current;
        if (!container) return;

        container.addEventListener('scroll', onScroll);
        return () => {
            checkBottom.cancel();
            container.removeEventListener('scroll', onScroll);
        };
    }, [containerRef, onScroll, checkBottom]);

    return { onScroll, isLoadingMore, loaderRef, reset };
};

export default useInfiniteScroll;
